import React, { useState } from "react";
import moment from "moment";
import { assetUrl, cleanArtifactFileName } from "../config/runtime";
import "./SidecarDrawer.css";

interface SidecarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  messages?: any[];
}

const TargetLineIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const CodeLineIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FileIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

type ArtifactType = "code" | "file";

interface ArtifactItem {
  id: string;
  type: ArtifactType;
  title: string;
  lang?: string;
  content: string;
  fileName?: string;
  sender: string;
}

export const SidecarDrawer: React.FC<SidecarDrawerProps> = ({
  isOpen,
  onClose,
  messages = [],
}) => {
  const [activeTab, setActiveTab] = useState<"location" | "artifact">("location");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const artifacts: ArtifactItem[] = [];

  messages.forEach((m, mIdx) => {
    const cleanMsg = m.content || "";
    const sender = m.role === "user" ? "用户" : "AI";

    // 1. 捕捉生成的 Markdown 文件/图片链接
    // scheme 必须可选：后端产出的是相对路径 [报告.md](/uploads/gen_xxx.md)，
    // 只认 https?:// 会让本抽屉恒为空，而气泡里的 FileCard 却照常渲染
    // (MessageContent 用的是宽松判断)，造成"文件生成了但成果区是 0"的假象。
    const fileLinkRegex = /(?:!\[([^\]]*)\]|\[([^\]]+)\])\(((?:https?:\/\/[^\s)]+?)?\/uploads\/[^\s)]+)\)/g;
    let fileMatch: RegExpExecArray | null;
    while ((fileMatch = fileLinkRegex.exec(cleanMsg)) !== null) {
      const rawLabel = (fileMatch[2] || fileMatch[1] || "").trim();
      // 相对路径补成当前 origin 的绝对地址，否则下载与"复制链接"给出的地址不可直接用
      const fileUrl = assetUrl(fileMatch[3]);
      // 图片那条产出的 alt 是 "生成的图表" 这类描述而非文件名，直接拿去当 download
      // 属性会存成一个没有扩展名的文件。标签不像文件名时退回 URL 里的真实名字。
      const urlBase = cleanArtifactFileName("", fileUrl);
      const fileName = /\.[a-z0-9]{1,8}$/i.test(rawLabel)
        ? cleanArtifactFileName(rawLabel, fileUrl)
        : urlBase || rawLabel || "产出文件";
      if (!artifacts.some((item) => item.content === fileUrl)) {
        artifacts.push({
          id: `file-${mIdx}-${artifacts.length}`,
          type: "file",
          title: fileName,
          fileName,
          content: fileUrl,
          sender,
        });
      }
    }

    // 2. 捕捉 Markdown 代码块 ```lang ... ```
    const fenceRegex = /```(\w*)\n?([\s\S]*?)```/g;
    let codeMatch: RegExpExecArray | null;
    while ((codeMatch = fenceRegex.exec(cleanMsg)) !== null) {
      const lang = codeMatch[1] || "code";
      const code = codeMatch[2].trim();
      if (code && !code.startsWith("[控制台输出]") && !code.startsWith("http")) {
        artifacts.push({
          id: `code-${mIdx}-${artifacts.length}`,
          type: "code",
          title: `${lang.toUpperCase()} 代码块`,
          lang,
          content: code,
          sender,
        });
      }
    }
  });

  const userMessages = messages
    .map((m, originalIdx) => ({ m, originalIdx }))
    .filter((item) => item.m.role === "user");

  const scrollToMessage = (originalIdx: number) => {
    const msgElements = document.querySelectorAll(".msg-row");
    if (msgElements && msgElements[originalIdx]) {
      msgElements[originalIdx].scrollIntoView({ behavior: "smooth", block: "center" });
      msgElements[originalIdx].classList.add("msg-row--highlighted");
      setTimeout(() => {
        msgElements[originalIdx]?.classList.remove("msg-row--highlighted");
      }, 1800);
    }
  };

  const handleCopy = (id: string, text: string) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const handleDownloadFile = async (fileName: string, fileUrl: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(fileUrl, "_blank");
    }
  };

  return (
    <aside className={`sidecar-drawer ${isOpen ? "" : "sidecar-drawer--closed"}`}>
      <div className="sidecar-drawer__inner">
        <div className="sidecar-drawer__header">
          {/* 💫 苹果 Segmented Control 物理滑动胶囊组 */}
          <div className="sidecar-drawer__tabs">
            <div
              className="sidecar-tab-indicator"
              style={{
                transform: activeTab === "location" ? "translateX(0%)" : "translateX(100%)",
              }}
            />

            <button
              type="button"
              className={`sidecar-drawer__tab ${
                activeTab === "location" ? "sidecar-drawer__tab--active" : ""
              }`}
              onClick={() => setActiveTab("location")}
            >
              <TargetLineIcon />
              <span>提问定位 ({userMessages.length})</span>
            </button>
            <button
              type="button"
              className={`sidecar-drawer__tab ${
                activeTab === "artifact" ? "sidecar-drawer__tab--active" : ""
              }`}
              onClick={() => setActiveTab("artifact")}
            >
              <CodeLineIcon />
              <span>成果/文件 ({artifacts.length})</span>
            </button>
          </div>

          <button
            type="button"
            className="sidecar-drawer__close"
            onClick={onClose}
            aria-label="关闭侧边栏"
          >
            ✕
          </button>
        </div>

        <div className="sidecar-drawer__content">
          {activeTab === "location" ? (
            <div className="sidecar-timeline sidecar-tab-panel">
              {userMessages.length > 0 ? (
                userMessages.map(({ m, originalIdx }) => {
                  const text = (m.content || "").trim();
                  const formattedTime = m.created_at ? moment(m.created_at).format("HH:mm") : "";

                  return (
                    <button
                      key={m.id || originalIdx}
                      type="button"
                      className="sidecar-card sidecar-card--clickable"
                      onClick={() => scrollToMessage(originalIdx)}
                    >
                      <div className="sidecar-card__row">
                        <span className="sidecar-card__text">{text || "(无文本内容)"}</span>
                        {formattedTime && <span className="sidecar-card__time">{formattedTime}</span>}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="sidecar-card">
                  <div className="sidecar-card__desc">暂无用户提问，输入消息后可快捷定位。</div>
                </div>
              )}
            </div>
          ) : (
            <div className="sidecar-artifact sidecar-tab-panel">
              {artifacts.length > 0 ? (
                artifacts.map((art) => (
                  <div key={art.id} className="sidecar-card sidecar-card--artifact">
                    <div className="sidecar-card__header-row">
                      <span className="sidecar-card__title">
                        {art.type === "file" ? <FileIcon /> : <CodeLineIcon />}
                        <span className="sidecar-card__title-text">{art.title}</span>
                      </span>
                      <span className="sidecar-card__sender">{art.sender}</span>
                    </div>

                    {art.type === "code" ? (
                      <>
                        <pre className="sidecar-card__pre">
                          <code>{art.content}</code>
                        </pre>
                        <div className="sidecar-card__actions">
                          <button
                            type="button"
                            className="sidecar-action-btn"
                            onClick={() => handleCopy(art.id, art.content)}
                          >
                            <CopyIcon />
                            <span>{copiedId === art.id ? "已复制" : "复制代码"}</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="sidecar-card__file-box">
                        <div className="sidecar-card__file-name">{art.fileName}</div>
                        <div className="sidecar-card__actions">
                          <button
                            type="button"
                            className="sidecar-action-btn sidecar-action-btn--primary"
                            onClick={() => handleDownloadFile(art.fileName || "download", art.content)}
                          >
                            <DownloadIcon />
                            <span>下载文件</span>
                          </button>
                          <button
                            type="button"
                            className="sidecar-action-btn"
                            onClick={() => handleCopy(art.id, art.content)}
                          >
                            <CopyIcon />
                            <span>{copiedId === art.id ? "已复制" : "复制链接"}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="sidecar-card">
                  <div className="sidecar-card__desc">当前对话中暂未产生代码成果、生成文件或 Markdown 代码块。</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SidecarDrawer;
